import axios from 'axios';
import { useQuery } from 'react-query';
import { WorkOrder } from '../models/workOrder';

// item 1446 (Chad, after PROP-100391 Sante Fe Bowling got a Deposit AND a Final generated 68 seconds
// apart and only the Final was finalized: "Don't stop invoices from being created, let's be louder
// about what step we are processing — deposit, 50%, final 50%, etc.") — ONE place that works out,
// for a work order, which invoice the next Generate will produce and what came before it. Mirrors
// InvoiceCreateAction's decision (proposal-driven always splits; standalone splits on an explicit
// percent; partially billed → the balance; fully billed → a fresh full bill) so the dialogs tell the
// truth. Read-only — nothing here blocks a generate.

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

export interface PriorInvoice {
  id: string;
  invoiceNumber: string | null;
  serviceName: string | null;
  isFinalized: boolean;
  status: number;
  discount: number | null;
  createdDate?: string | null;
  sentDate?: string | null;
}

export type InvoiceStep = 'deposit' | 'final' | 'full' | 'rebill' | 'backorder'; // item 1484 — backorder

export interface InvoicePlan {
  step: InvoiceStep;
  // the WO's full total and what this invoice bills
  total: number;
  billsNow: number;
  // the balance left AFTER this invoice (0 for full / final / rebill)
  balanceAfter: number;
  // deposit percent in charge (deposit/full steps only; null for final/rebill)
  percent: number | null;
  // earlier live invoices with the share each one billed
  prior: (PriorInvoice & { billed: number })[];
  billedSoFar: number;
  // earlier live invoices that are still Drafts — worth shouting about, never a block
  drafts: (PriorInvoice & { billed: number })[];
  proposalDriven: boolean;
}

interface PlanInputs {
  total: number;
  proposalDriven: boolean;
  proposalDepositPercent: number | null;
  prior: PriorInvoice[];
  // item 1484 — $ of ordered line quantity not yet on any live invoice (line-billed WOs only)
  remainingLineTotal?: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

// the backend's BilledSoFar rule, per invoice
const billedOf = (inv: PriorInvoice, total: number): number => (
  inv.discount != null && inv.discount !== 0 ? round2(total - Math.abs(inv.discount)) : total
);

// Pure: what will the NEXT generate produce? `overridePercent` = the dialog's typed/clicked %
// ('' → null = untouched). `explicitDiscount` = a typed discount (standalone WOs only) — the
// backend lets that win over the balance default.
export const computeInvoicePlan = (
  inputs: PlanInputs,
  overridePercent: number | null,
  explicitDiscount = 0,
): InvoicePlan => {
  const total = round2(inputs.total ?? 0);
  const prior = inputs.prior.map((p) => ({ ...p, billed: billedOf(p, total) }));
  const billedSoFar = round2(prior.reduce((a, p) => a + p.billed, 0));
  const drafts = prior.filter((p) => !p.isFinalized);
  const alreadyInvoiced = prior.length > 0;
  const partiallyBilled = alreadyInvoiced && billedSoFar > 0 && billedSoFar + 0.01 < total;
  const wantsSplit = inputs.proposalDriven || overridePercent != null || (partiallyBilled && explicitDiscount === 0);

  if (!alreadyInvoiced) {
    if (!wantsSplit) {
      return {
        step: 'full', total, billsNow: total, balanceAfter: 0, percent: 100, prior, billedSoFar, drafts, proposalDriven: inputs.proposalDriven,
      };
    }
    let pct = overridePercent ?? inputs.proposalDepositPercent ?? 50;
    pct = Math.min(100, Math.max(1, pct));
    if (pct >= 100) {
      return {
        step: 'full', total, billsNow: total, balanceAfter: 0, percent: 100, prior, billedSoFar, drafts, proposalDriven: inputs.proposalDriven,
      };
    }
    const deposit = round2(total * (pct / 100));
    return {
      step: 'deposit', total, billsNow: deposit, balanceAfter: round2(total - deposit), percent: pct, prior, billedSoFar, drafts, proposalDriven: inputs.proposalDriven,
    };
  }

  // item 1484 (SO-100732) — BACKORDER: earlier invoices billed by LINE (no split discount) and some
  // ordered quantity is still un-invoiced → the next invoice carries only the remaining lines. A
  // blank or 100% percent both mean "bill what is left" here (mirrors InvoiceCreateAction).
  const remaining = round2(inputs.remainingLineTotal ?? 0);
  const lineBilled = prior.every((p) => (p.discount ?? 0) === 0);
  if (!inputs.proposalDriven && lineBilled && remaining > 0.005 && explicitDiscount === 0
    && (overridePercent == null || overridePercent >= 100)) {
    return {
      step: 'backorder', total, billsNow: remaining, balanceAfter: 0, percent: null, prior, billedSoFar: round2(total - remaining), drafts, proposalDriven: false,
    };
  }

  if (wantsSplit && partiallyBilled) {
    return {
      step: 'final', total, billsNow: round2(total - billedSoFar), balanceAfter: 0, percent: null, prior, billedSoFar, drafts, proposalDriven: inputs.proposalDriven,
    };
  }
  // already fully billed (or a standalone WO with a typed discount): item 231's fresh full bill
  return {
    step: 'rebill', total, billsNow: total, balanceAfter: 0, percent: null, prior, billedSoFar, drafts, proposalDriven: inputs.proposalDriven,
  };
};

const fetchInputs = async (workOrderId: string): Promise<PlanInputs> => {
  const [woRes, invRes] = await Promise.all([
    axios.get(`${apiUrl()}WorkOrder/${workOrderId}`),
    axios.get(`${apiUrl()}invoice/WorkOrderId/${workOrderId}`),
  ]);
  const wo = woRes.data as WorkOrder;
  const prior = (invRes.data ?? []) as PriorInvoice[];
  // item 1484 — line quantities: ordered (WO lines) − invoiced (every live invoice's lines)
  let remainingLineTotal = 0;
  if (!wo.proposalId && prior.length > 0 && prior.every((p) => (p.discount ?? 0) === 0)) {
    type Line = { itemId?: string | null; sku?: string | null; description?: string | null; quantity?: number; retailEach?: number; taxEach?: number };
    const keyOf = (l: Line) => (l.itemId ? l.itemId.toLowerCase() : `${(l.sku ?? '').trim().toLowerCase()}|${(l.description ?? '').trim().toLowerCase()}`);
    const details = await Promise.all(prior.map((p) => axios.get(`${apiUrl()}invoice/${p.id}`).then((r) => r.data).catch(() => null)));
    const invoiced: Record<string, number> = {};
    details.forEach((d) => ((d?.invoiceItems ?? []) as Line[]).forEach((l) => { invoiced[keyOf(l)] = (invoiced[keyOf(l)] ?? 0) + (l.quantity ?? 0); }));
    ((wo.workOrderItems ?? []) as unknown as Line[]).forEach((l) => {
      const k = keyOf(l);
      const owed = Math.max(0, Math.min(l.quantity ?? 0, (l.quantity ?? 0) - (invoiced[k] ?? 0)));
      invoiced[k] = Math.max(0, (invoiced[k] ?? 0) - (l.quantity ?? 0));
      remainingLineTotal += owed * ((l.retailEach ?? 0) + (l.taxEach ?? 0));
    });
  }
  let proposalDepositPercent: number | null = null;
  if (wo.proposalId) {
    try {
      const { data } = await axios.get(`${apiUrl()}proposal/${wo.proposalId}`);
      proposalDepositPercent = (data as { depositPercent?: number | null }).depositPercent ?? null;
    } catch { proposalDepositPercent = null; }
  }
  return {
    total: wo.retailTotal ?? 0,
    proposalDriven: !!wo.proposalId,
    proposalDepositPercent,
    prior,
    remainingLineTotal,
  };
};

// Fetch the inputs for a WO (enabled only while a dialog is open) and expose the plan for a
// given override percent. `plan` is undefined until the inputs land.
const useInvoicePlan = (workOrderId: string | undefined, enabled: boolean, overridePercent: number | null) => {
  const q = useQuery(['invoicePlan', workOrderId], () => fetchInputs(workOrderId as string), {
    enabled: enabled && !!workOrderId,
    staleTime: 0,
    cacheTime: 0,
  });
  const plan = q.data ? computeInvoicePlan(q.data, overridePercent) : undefined;
  return { plan, isLoading: q.isLoading, error: q.error };
};

export default useInvoicePlan;
